import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Mic, MicOff, Volume2, VolumeX, Globe } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_OPTIONS = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'hi', label: 'हिंदी', flag: 'हि' },
];

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [chatLang, setChatLang] = useState(null); // null = show language picker
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [showPulse, setShowPulse] = useState(true);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Hide pulse after 10s
  useEffect(() => {
    const timer = setTimeout(() => setShowPulse(false), 10000);
    return () => clearTimeout(timer);
  }, []);

  const selectLanguage = (lang) => {
    setChatLang(lang);
    const welcomeMsg = lang === 'hi'
      ? 'Namaste, main Aarohi hoon — Divyadeep Skin Clinic se. Aapki skin ya hair se related koi bhi concern ho toh bataiye, main aapki help karungi.'
      : 'Hello, I\'m Aarohi from Divyadeep Skin Clinic. Please feel free to share your skin or hair concerns — I\'m here to assist you.';
    setMessages([{ role: 'bot', text: welcomeMsg, time: new Date() }]);
  };

  const speakText = useCallback((text, lang) => {
    if (!voiceEnabled || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang === 'hi' ? 'hi-IN' : 'en-IN';
    
    // Select best available female voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoices = lang === 'hi'
      ? ['Google हिन्दी', 'Microsoft Swara', 'Swara', 'Lekha', 'hi-IN', 'Hindi']
      : ['Google UK English Female', 'Microsoft Zira', 'Samantha', 'Karen', 'Veena', 'Rishi', 'en-IN'];
    
    let selectedVoice = null;
    for (const pref of preferredVoices) {
      selectedVoice = voices.find(v => v.name.includes(pref) && v.lang.includes(lang === 'hi' ? 'hi' : 'en'));
      if (selectedVoice) break;
    }
    if (!selectedVoice) {
      selectedVoice = voices.find(v => v.lang.includes(lang === 'hi' ? 'hi' : 'en'));
    }
    if (selectedVoice) utterance.voice = selectedVoice;
    
    utterance.rate = 0.88;
    utterance.pitch = 1.12;
    utterance.volume = 0.9;
    window.speechSynthesis.speak(utterance);
  }, [voiceEnabled]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const trimmed = text.trim();
    setMessages(prev => [...prev, { role: 'user', text: trimmed, time: new Date() }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chatbot`, {
        message: trimmed,
        session_id: sessionId,
        language: chatLang || 'en'
      });
      setSessionId(res.data.session_id);
      const botMsg = { role: 'bot', text: res.data.reply, time: new Date() };
      setMessages(prev => [...prev, botMsg]);
      speakText(res.data.reply, chatLang);
    } catch (err) {
      const errorMsg = chatLang === 'hi'
        ? 'Sorry, abhi kuch issue aa raha hai. Aap clinic call kar sakte hain: 083033 65990'
        : 'Sorry, facing a small issue right now. You can call us directly at 083033 65990.';
      setMessages(prev => [...prev, { role: 'bot', text: errorMsg, time: new Date() }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const toggleVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) return;

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = chatLang === 'hi' ? 'hi-IN' : 'en-IN';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setIsListening(false);
      sendMessage(transcript);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const switchLang = () => {
    const newLang = chatLang === 'en' ? 'hi' : 'en';
    setChatLang(newLang);
    const switchMsg = newLang === 'hi'
      ? 'Language Hindi mein change kar diya hai. Ab aap Hindi mein baat kar sakte hain.'
      : 'Switched to English. You can continue in English now.';
    setMessages(prev => [...prev, { role: 'bot', text: switchMsg, time: new Date() }]);
  };

  const formatTime = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const handleOpen = () => {
    setIsOpen(true);
    setShowPulse(false);
  };

  return (
    <>
      {/* ===== FLOATING CHAT BUTTON ===== */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-4 md:right-6 z-[99999] group"
          data-testid="chatbot-toggle"
        >
          <div className="relative">
            {/* Pulse rings */}
            {showPulse && (
              <>
                <div className="absolute inset-0 rounded-full bg-[#D1A760] animate-ping opacity-20" />
                <div className="absolute -inset-1 rounded-full bg-[#D1A760]/10 animate-pulse" />
              </>
            )}
            <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#D1A760] to-[#B89255] text-white shadow-[0_8px_30px_rgba(209,167,96,0.35)] flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
              <MessageCircle className="w-6 h-6" />
            </div>
          </div>
          {/* Tooltip */}
          <div className="absolute bottom-full right-0 mb-3 whitespace-nowrap bg-white rounded-2xl shadow-lg px-4 py-2.5 text-xs font-manrope text-[#0F172A] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              {chatLang === 'hi' ? 'Skin related koi sawal?' : 'Have a skin concern? Ask us!'}
            </div>
            <div className="absolute bottom-0 right-6 translate-y-1/2 rotate-45 w-2.5 h-2.5 bg-white border-r border-b border-slate-100" />
          </div>
        </button>
      )}

      {/* ===== CHAT WINDOW ===== */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-4 md:right-6 z-[99999] w-[380px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-6rem)] bg-white rounded-3xl shadow-[0_24px_80px_rgba(0,0,0,0.18)] border border-slate-100 flex flex-col overflow-hidden"
          style={{ animation: 'chatSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
          data-testid="chatbot-window"
        >
          {/* ===== HEADER ===== */}
          <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D1A760] to-[#B89255] flex items-center justify-center text-white font-outfit font-semibold text-sm">
                    D
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-[#0F172A]" />
                </div>
                <div>
                  <p className="text-white font-outfit font-medium text-sm">Aarohi</p>
                  <p className="text-white/50 text-[11px] font-manrope">Clinic Assistant &middot; Online</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {chatLang && (
                  <>
                    <button
                      onClick={switchLang}
                      className="p-2 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-all"
                      data-testid="chatbot-lang-toggle"
                      title="Switch language"
                    >
                      <Globe className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setVoiceEnabled(!voiceEnabled)}
                      className="p-2 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-all"
                      data-testid="chatbot-voice-toggle"
                      title={voiceEnabled ? 'Mute voice' : 'Enable voice'}
                    >
                      {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                    </button>
                  </>
                )}
                <button
                  onClick={() => { setIsOpen(false); window.speechSynthesis?.cancel(); }}
                  className="p-2 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-all"
                  data-testid="chatbot-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* ===== LANGUAGE PICKER (Step 1) ===== */}
          {!chatLang && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 bg-[#FAFAFA]" data-testid="chatbot-lang-picker">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D1A760] to-[#B89255] flex items-center justify-center mb-6 shadow-[0_8px_24px_rgba(209,167,96,0.25)]">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <p className="font-outfit font-medium text-[#0F172A] text-base text-center mb-1">
                Welcome to Divyadeep Skin
              </p>
              <p className="text-xs text-[#475569] font-manrope text-center mb-1">
                Divyadeep Skin clinic mein aapka swagat hai
              </p>
              <p className="text-xs text-[#94A3B8] font-manrope text-center mb-8">
                Please choose your preferred language
              </p>
              <div className="flex gap-3 w-full max-w-xs">
                {LANG_OPTIONS.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => selectLanguage(lang.code)}
                    className="flex-1 py-4 rounded-2xl bg-white border-2 border-slate-100 hover:border-[#D1A760] hover:shadow-[0_4px_20px_rgba(209,167,96,0.15)] transition-all duration-200 flex flex-col items-center gap-2"
                    data-testid={`chatbot-lang-${lang.code}`}
                  >
                    <span className="text-lg font-outfit font-semibold text-[#0F172A]">{lang.flag}</span>
                    <span className="text-xs font-manrope text-[#475569]">{lang.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ===== MESSAGES ===== */}
          {chatLang && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#FAFAFA]" data-testid="chatbot-messages">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
                    style={{ animation: 'msgFadeIn 0.3s ease-out' }}
                  >
                    <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {msg.role === 'bot' && (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#D1A760] to-[#B89255] flex items-center justify-center mr-2 flex-shrink-0 mt-1">
                          <span className="text-white text-[10px] font-outfit font-semibold">D</span>
                        </div>
                      )}
                      <div className="max-w-[75%]">
                        <div
                          className={`px-4 py-3 text-[13px] font-manrope leading-relaxed ${
                            msg.role === 'user'
                              ? 'bg-[#0F172A] text-white rounded-2xl rounded-br-sm'
                              : 'bg-white text-[#0F172A] rounded-2xl rounded-bl-sm shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100'
                          }`}
                          data-testid={`chat-message-${idx}`}
                        >
                          {msg.text}
                        </div>
                        <p className={`text-[10px] mt-1 font-manrope ${msg.role === 'user' ? 'text-right text-[#94A3B8]' : 'text-[#94A3B8] ml-1'}`}>
                          {formatTime(msg.time)}
                        </p>
                      </div>
                    </div>
                    {/* CTA buttons after every bot message (except welcome) */}
                    {msg.role === 'bot' && idx > 0 && (
                      <div className="flex gap-2 ml-9 mt-1.5" data-testid={`chat-cta-${idx}`}>
                        <button
                          onClick={() => {
                            setIsOpen(false);
                            // Try to detect treatment from bot message and pre-fill
                            const treatments = ['Acne Treatment', 'Pigmentation', 'Hair Transplant', 'Laser Treatment', 'Botox', 'Chemical Peeling', 'Skin Diseases', 'Scar Treatment'];
                            const found = treatments.find(t => msg.text.toLowerCase().includes(t.toLowerCase()));
                            if (found) {
                              window.dispatchEvent(new CustomEvent('selectTreatment', { detail: found }));
                            }
                            document.getElementById('appointment')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="px-3 py-1.5 rounded-full bg-[#25D366] text-white text-[11px] font-manrope font-medium shadow-sm hover:shadow-md transition-all flex items-center gap-1"
                          data-testid={`chat-book-btn-${idx}`}
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          Book on WhatsApp
                        </button>
                        <a
                          href="tel:+918303365990"
                          className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[11px] font-manrope text-[#475569] hover:border-[#D1A760] transition-all"
                        >
                          Call
                        </a>
                      </div>
                    )}
                  </div>
                ))}

                {/* Typing Indicator */}
                {loading && (
                  <div className="flex justify-start" style={{ animation: 'msgFadeIn 0.2s ease-out' }}>
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#D1A760] to-[#B89255] flex items-center justify-center mr-2 flex-shrink-0">
                      <span className="text-white text-[10px] font-outfit font-semibold">D</span>
                    </div>
                    <div className="bg-white px-5 py-3.5 rounded-2xl rounded-bl-sm shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100">
                      <div className="flex gap-1.5 items-center">
                        <div className="w-2 h-2 bg-[#D1A760] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-[#D1A760] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-[#D1A760] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* ===== QUICK ACTIONS (show after language selection, before first user msg) ===== */}
              {messages.length <= 1 && !loading && (
                <div className="px-4 pb-2 bg-[#FAFAFA]" data-testid="chatbot-quick-actions">
                  <div className="flex flex-wrap gap-2">
                    {(chatLang === 'hi' ? [
                      'Mere face pe acne ho rahe hain',
                      'Hair fall bahut ho raha hai',
                      'Pigmentation ka treatment?',
                      'Appointment book karna hai'
                    ] : [
                      'I have acne problems',
                      'Hair fall treatment?',
                      'Pigmentation on face',
                      'Book an appointment'
                    ]).map((q, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(q)}
                        className="px-3.5 py-2 rounded-full bg-white border border-slate-200 text-[11px] font-manrope text-[#475569] hover:border-[#D1A760] hover:text-[#D1A760] transition-all duration-200"
                        data-testid={`quick-action-${i}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ===== INPUT BAR ===== */}
              <div className="px-4 py-3 bg-white border-t border-slate-100" data-testid="chatbot-input-area">
                <div className="flex items-center gap-2">
                  {/* Mic Button */}
                  <button
                    onClick={toggleVoiceInput}
                    className={`p-2.5 rounded-full transition-all duration-200 flex-shrink-0 ${
                      isListening
                        ? 'bg-red-500 text-white shadow-[0_0_20px_rgba(239,68,68,0.3)] animate-pulse'
                        : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                    }`}
                    data-testid="chatbot-voice-btn"
                    title={isListening ? 'Listening... tap to stop' : 'Tap to speak'}
                  >
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>

                  {/* Text Input */}
                  <div className="flex-1 relative">
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
                      placeholder={chatLang === 'hi' ? 'Apna sawal yahan likhein...' : 'Type your concern here...'}
                      className="w-full px-4 py-2.5 rounded-full bg-[#F1F5F9] text-[13px] font-manrope text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#D1A760]/20 focus:bg-white transition-all"
                      data-testid="chatbot-input"
                      disabled={loading}
                    />
                    {isListening && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="flex gap-0.5 items-center">
                          <div className="w-1 h-3 bg-red-400 rounded-full animate-pulse" />
                          <div className="w-1 h-4 bg-red-500 rounded-full animate-pulse" style={{ animationDelay: '75ms' }} />
                          <div className="w-1 h-2 bg-red-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Send Button */}
                  <button
                    onClick={() => sendMessage(input)}
                    disabled={!input.trim() || loading}
                    className="p-2.5 rounded-full bg-gradient-to-br from-[#D1A760] to-[#B89255] text-white disabled:opacity-30 hover:shadow-[0_4px_12px_rgba(209,167,96,0.3)] transition-all duration-200 flex-shrink-0"
                    data-testid="chatbot-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>

                {/* Powered by line */}
                <p className="text-center text-[9px] text-[#CBD5E1] font-manrope mt-2">
                  Divyadeep Skin, Laser & Hair Transplant Center
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== ANIMATIONS ===== */}
      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes msgFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
